import React, { useState, useEffect, useCallback } from 'react';

const CountdownTimer = ({ 
    randomExpires,           // Date d'expiration du code (randomExpires)
    experyDate = null,       // Date d'expiration du token (optionnel)
    createdAt = null,        // Date de création (optionnel)
    onExpired = () => {},
    enableAnimation = false,
    showProgressBar = true,
    showTimeText = true,
    warningThreshold = 300,  // 5 minutes en secondes
    dangerThreshold = 60,    // 1 minute en secondes
    infoSource = null        // Pour les traductions
}) => {
    const [timeLeft, setTimeLeft] = useState(null);
    const [totalDuration, setTotalDuration] = useState(null);
    const [isExpired, setIsExpired] = useState(false);
    const [status, setStatus] = useState('normal'); // 'normal', 'warning', 'danger', 'expired'

    // Convertir la date MongoDB en timestamp
    const parseMongoDate = (dateValue) => {
        if (!dateValue) return null;
        
        // Si c'est un objet avec $date (format EJSON)
        if (dateValue.$date) {
            return new Date(dateValue.$date).getTime();
        }
        
        // Si c'est une string ISO
        if (typeof dateValue === 'string') {
            return new Date(dateValue).getTime();
        }
        
        // Si c'est déjà un timestamp
        if (typeof dateValue === 'number') {
            return dateValue;
        }
        
        // Si c'est un objet Date
        if (dateValue instanceof Date) {
            return dateValue.getTime();
        }
        
        return null;
    };

    // Calculer le temps restant
    const calculateTimeLeft = useCallback(() => {
        const now = Date.now();
        const expiryTime = parseMongoDate(randomExpires);
        
        if (!expiryTime) return 0;
        
        const difference = expiryTime - now;
        return Math.max(0, Math.floor(difference / 1000));
    }, [randomExpires]);

    // Calculer la durée totale
    const calculateTotalDuration = useCallback(() => {
        const expiryTime = parseMongoDate(randomExpires);
        
        if (createdAt) {
            const startTime = parseMongoDate(createdAt);
            if (startTime && expiryTime) {
                return Math.floor((expiryTime - startTime) / 1000);
            }
        }
        
        // Par défaut, calculer basé sur 20 minutes avant expiration
        // ou utiliser une durée fixe
        return 20 * 60; // 20 minutes par défaut
    }, [createdAt, randomExpires]);

    // Initialisation
    useEffect(() => {
        const total = calculateTotalDuration();
        const remaining = calculateTimeLeft();
        
        setTotalDuration(total);
        setTimeLeft(remaining);
        
        // Vérifier si déjà expiré
        if (remaining <= 0) {
            setIsExpired(true);
            setStatus('expired');
            onExpired();
        }
    }, [calculateTimeLeft, calculateTotalDuration, onExpired]);

    // Mise à jour du compte à rebours
    useEffect(() => {
        if (timeLeft === null || isExpired) return;

        if (timeLeft <= 0) {
            setIsExpired(true);
            setStatus('expired');
            onExpired();
            return;
        }

        // Déterminer le statut basé sur le temps restant
        if (timeLeft <= dangerThreshold) {
            setStatus('danger');
        } else if (timeLeft <= warningThreshold) {
            setStatus('warning');
        } else {
            setStatus('normal');
        }

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                const newTime = prev - 1;
                
                if (newTime <= 0) {
                    clearInterval(timer);
                    setIsExpired(true);
                    setStatus('expired');
                    onExpired();
                    return 0;
                }
                
                // Mettre à jour le statut
                if (newTime <= dangerThreshold) {
                    setStatus('danger');
                } else if (newTime <= warningThreshold) {
                    setStatus('warning');
                }
                
                return newTime;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft, isExpired, onExpired, warningThreshold, dangerThreshold]);

    // Formater le temps
    const formatTime = (seconds) => {
        if (seconds <= 0) return '00:00';
        
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hours > 0) {
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Calculer le pourcentage de progression
    const getProgressPercentage = () => {
        if (!totalDuration || totalDuration === 0 || isExpired) return 0;
        return Math.max(0, Math.min(100, (timeLeft / totalDuration) * 100));
    };

    // Obtenir le texte traduit ou par défaut
    const getText = (key, defaultText) => {
        if (infoSource && infoSource.laguangeElakay) {
            const text = infoSource.laguangeElakay('countdown', infoSource.languageAPI)?.[key];
            return text || defaultText;
        }
        return defaultText;
    };

    // Loading state
    if (timeLeft === null) {
        return (
            <div className="sign-countdown-wrapper">
                <div className="sign-countdown-loading">
                    <div className="sign-dot"></div>
                    <div className="sign-dot"></div>
                    <div className="sign-dot"></div>
                </div>
            </div>
        );
    }

    return (
        <div className={`sign-countdown-wrapper ${status} ${enableAnimation ? '' : 'no-animation'}`}>
            {/* Header */}
            <div className="sign-countdown-header">
                <div className={`sign-countdown-icon-wrapper ${status}`}>
                    {isExpired ? (
                        <>
                        <svg className="sign-countdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="15" y1="9" x2="9" y2="15"/>
                            <line x1="9" y1="9" x2="15" y2="15"/>
                        </svg>
                        </>
                    ) : (
                        <svg className="sign-countdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12,6 12,12 16,14"/>
                        </svg>
                    )}
                </div>
                <span className="sign-countdown-label">
                    {isExpired 
                        ? getText('expired', 'Code Expired') 
                        : getText('timeRemaining', 'Time Remaining')
                    }
                </span>
            </div>

            {/* Time Display */}
            {showTimeText && (
                <div className={`sign-countdown-time ${status}`}>
                    <span className="sign-countdown-digits">
                        {formatTime(timeLeft)}
                    </span>
                    {!isExpired && status === 'danger' && (
                        <span className="sign-countdown-urgent">
                            {getText('hurryUp', 'Hurry up!')}
                        </span>
                    )}
                </div>
            )}

            {/* Progress Bar */}
            {showProgressBar && (
                <div className="sign-countdown-progress-wrapper">
                    <div className="sign-countdown-progress-bg">
                        <div 
                            className={`sign-countdown-progress-fill ${status} ${enableAnimation ? 'animated' : ''}`}
                            style={{ width: `${getProgressPercentage()}%` }}
                        >
                            {enableAnimation && !isExpired && status !== 'danger' && (
                                <div className="sign-countdown-progress-shine"></div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Status Message */}
            <div className={`sign-countdown-status ${status}`}>
                {isExpired ? (
                    <p className="sign-countdown-message expired">
                        <svg className="sign-countdown-message-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                            <line x1="12" y1="9" x2="12" y2="13"/>
                            <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                        {getText('expiredMessage', 'Your verification code has expired. Please request a new one.')}
                    </p>
                ) : status === 'danger' ? (
                    <p className="sign-countdown-message danger">
                        <svg className="sign-countdown-message-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        {getText('dangerMessage', 'Less than 1 minute left! Enter your code now.')}<br/>
                         {infoSource.laguangeElakay('signinPage', infoSource.languageAPI).text2}
                    </p>
                ) : status === 'warning' ? (
                    <p className="sign-countdown-message warning">
                        <svg className="sign-countdown-message-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12,6 12,12 16,14"/>
                        </svg>
                        {getText('warningMessage', 'Time is running out. Please enter your code soon.')}<br/>
                         {infoSource.laguangeElakay('signinPage', infoSource.languageAPI).text2}
                    </p>
                ) : (
                    <p className="sign-countdown-message normal">
                        {getText('normalMessage', 'Enter the 6-digit code sent to your email.')} <br/>
                         {infoSource.laguangeElakay('signinPage', infoSource.languageAPI).text2}
                    </p>
                )}
            </div>
        </div>
    );
};

export default CountdownTimer;
